import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Edit2, ToggleLeft, ToggleRight, Tag, X, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  code: "", type: "percent", value: 10,
  min_order: 0, max_discount: "", max_uses: 0,
  enabled: true, expires_at: "", description: "",
};

function Badge({ enabled }) {
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
      {enabled ? "Active" : "Disabled"}
    </span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, type = "text", placeholder = "", min }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      min={min}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500"
    />
  );
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "create" | coupon_object (for edit)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("it_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/coupons`, { headers });
      setCoupons(res.data);
    } catch { toast.error("Failed to load coupons"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setModal("create"); };
  const openEdit = (c) => {
    setForm({ ...c, max_discount: c.max_discount ?? "", expires_at: c.expires_at ?? "" });
    setModal(c);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error("Coupon code is required"); return; }
    if (!form.value) { toast.error("Discount value is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase().trim(),
        value: Number(form.value),
        min_order: Number(form.min_order || 0),
        max_discount: form.max_discount !== "" ? Number(form.max_discount) : null,
        max_uses: Number(form.max_uses || 0),
      };
      if (modal === "create") {
        await axios.post(`${API}/admin/coupons`, payload, { headers });
        toast.success("Coupon created");
      } else {
        await axios.put(`${API}/admin/coupons/${modal.id}`, payload, { headers });
        toast.success("Coupon updated");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save coupon");
    } finally { setSaving(false); }
  };

  const handleToggle = async (c) => {
    try {
      await axios.put(`${API}/admin/coupons/${c.id}`, { enabled: !c.enabled }, { headers });
      setCoupons(cs => cs.map(x => x.id === c.id ? { ...x, enabled: !x.enabled } : x));
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${c.id}`, { headers });
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
            <Tag className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Coupon Codes</h1>
            <p className="text-xs text-slate-500">{coupons.length} coupon{coupons.length !== 1 ? "s" : ""} — create % or flat discounts for checkout</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-slate-400 hover:text-slate-600 transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={openCreate}
            data-testid="coupon-create-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-all shadow-sm"
          >
            <Plus className="w-4 h-4" /> New Coupon
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-16 text-center">
          <Tag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm font-medium">No coupons yet</p>
          <p className="text-slate-400 text-xs mt-0.5">Create your first coupon to offer discounts at checkout</p>
          <button onClick={openCreate} className="mt-4 px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-all">
            Create Coupon
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 font-semibold border-b border-slate-100 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Discount</th>
                <th className="px-4 py-3 text-left">Min Order</th>
                <th className="px-4 py-3 text-left">Uses</th>
                <th className="px-4 py-3 text-left">Expires</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {coupons.map(c => (
                <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-mono font-bold text-orange-600 text-sm tracking-wider">{c.code}</span>
                      {c.description && <p className="text-[10px] text-slate-400 mt-0.5">{c.description}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {c.type === "percent"
                      ? `${c.value}%${c.max_discount ? ` (max ₹${c.max_discount})` : ""}`
                      : `₹${c.value}`}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {c.min_order > 0 ? `₹${c.min_order}` : "None"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {c.uses}{c.max_uses > 0 ? ` / ${c.max_uses}` : ""}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {c.expires_at ? c.expires_at.slice(0, 10) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggle(c)}>
                      <Badge enabled={c.enabled} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1.5 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-all"
                        title="Edit"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modal && (
        <Modal
          title={modal === "create" ? "Create Coupon" : `Edit — ${modal.code}`}
          onClose={() => setModal(null)}
        >
          <div className="space-y-3">
            <Field label="Coupon Code *" hint="Uppercase only — e.g. SAVE10, WELCOME50">
              <input
                type="text"
                value={form.code}
                onChange={e => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
                placeholder="SAVE10"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono font-bold tracking-wider focus:outline-none focus:border-orange-500"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Discount Type">
                <select
                  value={form.type}
                  onChange={e => set("type", e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 bg-white"
                >
                  <option value="percent">Percentage (%)</option>
                  <option value="flat">Flat Amount (₹)</option>
                </select>
              </Field>
              <Field label={form.type === "percent" ? "Value (%)" : "Value (₹)"}>
                <Input type="number" min={0} value={form.value} onChange={v => set("value", v)} placeholder={form.type === "percent" ? "10" : "100"} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Min Order (₹)" hint="0 = no minimum">
                <Input type="number" min={0} value={form.min_order} onChange={v => set("min_order", v)} placeholder="0" />
              </Field>
              {form.type === "percent" && (
                <Field label="Max Discount (₹)" hint="blank = no cap">
                  <Input type="number" min={0} value={form.max_discount} onChange={v => set("max_discount", v)} placeholder="e.g. 200" />
                </Field>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Max Uses" hint="0 = unlimited">
                <Input type="number" min={0} value={form.max_uses} onChange={v => set("max_uses", v)} placeholder="0" />
              </Field>
              <Field label="Expiry Date" hint="optional">
                <Input type="date" value={form.expires_at} onChange={v => set("expires_at", v)} />
              </Field>
            </div>

            <Field label="Description (optional)" hint="Shown to customers at checkout">
              <Input value={form.description} onChange={v => set("description", v)} placeholder="e.g. 10% off on your first order" />
            </Field>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => set("enabled", !form.enabled)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${form.enabled ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-400 border-slate-300"}`}
              >
                {form.enabled ? <><ToggleRight className="w-3.5 h-3.5" /> Active</> : <><ToggleLeft className="w-3.5 h-3.5" /> Disabled</>}
              </button>
              <span className="text-xs text-slate-400">{form.enabled ? "Coupon is active at checkout" : "Coupon is hidden from checkout"}</span>
            </div>
          </div>

          <div className="flex gap-2 mt-5">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 flex-1 justify-center px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
            >
              {saving
                ? <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : (modal === "create" ? "Create Coupon" : "Save Changes")}
            </button>
            <button onClick={() => setModal(null)} className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-all">
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
